// components/canvas-surface/canvas-gallery.js
// ═══════════════════════════════════════════════════════════
// Sub-components for the Canvas Gallery view:
//   CanvasPanelHeader — top bar, adapts to Focus vs Gallery mode
//   StreamingOverlay  — live artifact build (spinner + iframe)
//   MultiSelectBar    — floating action bar when tiles are selected
//   EmptyState        — shown when no files exist
//   FileTile          — single artifact tile card
//   KindIcon          — SVG icon for a given artifact kind
//   NewArtifactMenu   — dropdown for creating blank artifacts
//   ModeToggleButton  — single segment in the Read/Edit toggle
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { FILE_NODE_COLORS } from '../../lib/workspace-config';
import { classifyArtifact } from '../../lib/canvas-classify';

// ── Canvas panel header ──────────────────────────────────────
// Top bar that adapts between Focus Mode (back button, title, download)
// and Gallery Mode (Canvas pill, New button, Select toggle, file count).
export function CanvasPanelHeader({
  inFocusMode,
  focusedNode,
  focusedArtifact,
  labels,
  onClearFocus,
  onDownload,
  atLimit,
  warning,
  fileCount,
  fileLimit,
  hasFiles,
  showNewMenu,
  setShowNewMenu,
  onCreateBlank,
  selectMode,
  setSelectMode,
  setSelectedIds,
}) {
  return (
    <div style={{
      height: 36,
      borderBottom: '1px solid #1e1e2a',
      display: 'flex',
      alignItems: 'center',
      padding: '0 14px',
      gap: 10,
      flexShrink: 0,
      background: '#07070a',
    }}>
      {inFocusMode ? (
        <>
          {/* Back button */}
          <button
            onClick={onClearFocus}
            title="Back to gallery (Esc)"
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: 11,
              fontFamily: 'Georgia, serif',
              cursor: 'pointer',
              padding: '2px 6px',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8a96e'}
            onMouseLeave={e => e.currentTarget.style.color = '#888'}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>‹</span> Gallery
          </button>
          {/* Mode pill */}
          <div style={{
            fontSize: 9,
            padding: '2px 9px',
            borderRadius: 10,
            border: '1px solid rgba(200,169,110,0.25)',
            background: 'rgba(200,169,110,0.08)',
            color: '#c8a96e',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'Georgia, serif',
          }}>
            ◎ {classifyArtifact(focusedArtifact, focusedNode)}
          </div>
          {/* Title */}
          <div style={{
            flex: 1,
            fontSize: 12,
            color: '#d4d4d4',
            fontFamily: 'Georgia, serif',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {focusedNode.title || 'Untitled'}
          </div>
          {/* Label badge */}
          <span style={{
            fontSize: 9,
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            color: FILE_NODE_COLORS[focusedNode.file_source] || FILE_NODE_COLORS.uploaded,
            letterSpacing: '0.06em',
          }}>
            {labels[focusedNode.id]}
          </span>
          {/* Download button */}
          <button
            onClick={onDownload}
            title="Download artifact"
            style={{
              background: 'none',
              border: '1px solid #2a2a2a',
              borderRadius: 4,
              color: '#888',
              fontSize: 9,
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#7a9e82'; e.currentTarget.style.borderColor = '#7a9e82'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 1v8M3 6l3 3 3-3M1 11h10" />
            </svg>
            Download
          </button>
        </>
      ) : (
        <>
          <div style={{
            fontSize: 9,
            padding: '2px 9px',
            borderRadius: 10,
            border: '1px solid rgba(200,169,110,0.25)',
            background: 'rgba(200,169,110,0.08)',
            color: '#c8a96e',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'Georgia, serif',
          }}>
            ◎ Canvas
          </div>
          <div style={{ flex: 1 }} />
          {/* New artifact button */}
          {!atLimit && onCreateBlank && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNewMenu(v => !v)}
                title="Create new artifact"
                style={{
                  background: showNewMenu ? 'rgba(122,158,130,0.1)' : 'none',
                  border: `1px solid ${showNewMenu ? '#7a9e82' : '#2a2a2a'}`,
                  borderRadius: 4,
                  color: showNewMenu ? '#7a9e82' : '#888',
                  fontSize: 9,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                onMouseEnter={e => { if (!showNewMenu) { e.currentTarget.style.color = '#7a9e82'; e.currentTarget.style.borderColor = '#7a9e82'; }}}
                onMouseLeave={e => { if (!showNewMenu) { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#2a2a2a'; }}}
              >
                <span style={{ fontSize: 12, lineHeight: 1 }}>+</span> New
              </button>
              {showNewMenu && (
                <NewArtifactMenu
                  onSelect={(kind) => { setShowNewMenu(false); onCreateBlank(kind); }}
                  onClose={() => setShowNewMenu(false)}
                />
              )}
            </div>
          )}
          {/* Select mode toggle */}
          {hasFiles && (
            <button
              onClick={() => {
                setSelectMode(v => !v);
                if (selectMode) setSelectedIds(new Set());
              }}
              title={selectMode ? 'Cancel selection' : 'Select files'}
              style={{
                background: selectMode ? 'rgba(155,143,196,0.1)' : 'none',
                border: `1px solid ${selectMode ? '#9b8fc4' : '#2a2a2a'}`,
                borderRadius: 4,
                color: selectMode ? '#9b8fc4' : '#888',
                fontSize: 9,
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '3px 8px',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { if (!selectMode) { e.currentTarget.style.color = '#9b8fc4'; e.currentTarget.style.borderColor = '#9b8fc4'; }}}
              onMouseLeave={e => { if (!selectMode) { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#2a2a2a'; }}}
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          )}
          <div style={{
            fontSize: 10,
            color: atLimit ? '#c46050' : warning ? '#c8a96e' : '#555',
            fontFamily: 'Georgia, serif',
          }}>
            {fileCount} / {fileLimit} files
          </div>
        </>
      )}
    </div>
  );
}

// ── Streaming overlay ────────────────────────────────────────
// Shown while Shift is building an artifact in real-time.
// Two states: spinner (fence opened, no content yet) and live iframe
// (tokens streaming in). Stays visible as a bridge until Focus Mode
// takes over after the artifact is saved.
export function StreamingOverlay({ streamingContent, streamingDone, aiName }) {
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      top: 36,
      zIndex: 10,
      background: '#07070a',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {streamingContent ? (
        /* Live iframe — updates as tokens stream in, stays visible
           as bridge until Focus Mode takes over. No gap. */
        <div style={{ flex: 1, padding: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Status bar — shows while building, hides when done */}
          {!streamingDone && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 4px',
              marginBottom: 6,
              flexShrink: 0,
            }}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#c8a96e',
                animation: 'ws-canvas-pulse 1.2s ease-in-out infinite',
              }} />
              <span style={{
                fontSize: 10,
                color: '#c8a96e',
                fontFamily: 'Georgia, serif',
                fontWeight: 600,
                letterSpacing: '0.04em',
              }}>
                {aiName} is building...
              </span>
            </div>
          )}
          <iframe
            srcDoc={streamingContent.content}
            sandbox="allow-scripts"
            title="Canvas streaming"
            style={{
              flex: 1,
              width: '100%',
              minHeight: 0,
              border: '1px solid #272736',
              borderRadius: 6,
              background: '#07070a',
            }}
          />
        </div>
      ) : (
        /* Spinner — fence opened but no content yet */
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: '2px solid rgba(200,169,110,0.3)',
            borderTopColor: '#c8a96e',
            animation: 'ws-canvas-spinner 1s linear infinite',
          }} />
          <div style={{
            fontSize: 13,
            color: '#c8a96e',
            fontFamily: 'Georgia, serif',
            fontWeight: 600,
          }}>
            {aiName} is preparing...
          </div>
        </div>
      )}
      <style jsx>{`
        @keyframes ws-canvas-spinner {
          to { transform: rotate(360deg); }
        }
        @keyframes ws-canvas-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// ── Multi-select action bar ──────────────────────────────────
// Floating bar at the bottom of the gallery when tiles are selected.
// Shows count, download-all and delete-all actions.
export function MultiSelectBar({ selectedIds, fileNodes, artifactsById, onDownload, onDeleteAll }) {
  return (
    <div style={{
      position: 'absolute',
      bottom: 16,
      left: 16,
      right: 16,
      background: '#111118',
      border: '1px solid #2a2a2a',
      borderRadius: 10,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      zIndex: 5,
    }}>
      <span style={{
        fontSize: 11,
        color: '#d4d4d4',
        fontFamily: 'Georgia, serif',
        fontWeight: 600,
      }}>
        {selectedIds.size} selected
      </span>
      <div style={{ flex: 1 }} />
      <button
        onClick={onDownload}
        style={{
          background: 'none',
          border: '1px solid #2a2a2a',
          borderRadius: 5,
          color: '#7a9e82',
          fontSize: 9,
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '5px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#7a9e82'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a2a'}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 1v8M3 6l3 3 3-3M1 11h10" />
        </svg>
        Download
      </button>
      <button
        onClick={onDeleteAll}
        style={{
          background: 'none',
          border: '1px solid #2a2a2a',
          borderRadius: 5,
          color: '#c46050',
          fontSize: 9,
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '5px 12px',
          cursor: 'pointer',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#c46050'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a2a'}
      >
        Delete
      </button>
    </div>
  );
}

// ── Gallery body ─────────────────────────────────────────────
// Drag-and-drop zone containing the tile grid and the multi-select
// action bar. Handles all layout for Gallery Mode.
export function GalleryBody({
  dragOver,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  transition,
  hasFiles,
  fileNodes,
  labels,
  artifactsById,
  selectedTileIdx,
  selectMode,
  selectedIds,
  setSelectedIds,
  onOpenTile,
  onDelete,
  workspaceGoal,
  atLimit,
  onDownloadSelected,
  onDeleteAll,
}) {
  return (
    <div
      className="ws-canvas-scroll"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        flex: 1,
        overflowY: 'auto',
        background: dragOver ? 'rgba(200,169,110,0.04)' : 'transparent',
        border: dragOver ? '2px dashed rgba(200,169,110,0.3)' : '2px dashed transparent',
        opacity: transition === 'focus-out' ? 0 : 1,
        transition: 'background 0.12s, border 0.12s, opacity 0.2s ease-out',
        boxSizing: 'border-box',
      }}
    >
      {!hasFiles ? (
        <EmptyState dragOver={dragOver} workspaceGoal={workspaceGoal} atLimit={atLimit} />
      ) : (
        <div style={{
          padding: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 14,
          alignContent: 'start',
        }}>
          {fileNodes.map((node, idx) => (
            <FileTile
              key={node.id}
              node={node}
              label={labels[node.id]}
              artifact={node.artifact_id ? artifactsById[node.artifact_id] : null}
              isKeyboardSelected={idx === selectedTileIdx}
              selectMode={selectMode}
              isSelected={selectedIds.has(node.id)}
              onClick={() => {
                if (selectMode) {
                  setSelectedIds(prev => {
                    const next = new Set(prev);
                    next.has(node.id) ? next.delete(node.id) : next.add(node.id);
                    return next;
                  });
                } else {
                  onOpenTile(node);
                }
              }}
              onDelete={() => onDelete(node)}
            />
          ))}
        </div>
      )}

      {/* Multi-select action bar */}
      {selectMode && selectedIds.size > 0 && (
        <MultiSelectBar
          selectedIds={selectedIds}
          fileNodes={fileNodes}
          artifactsById={artifactsById}
          onDownload={onDownloadSelected}
          onDeleteAll={onDeleteAll}
        />
      )}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────
export function EmptyState({ dragOver, workspaceGoal, atLimit }) {
  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
    }}>
      <div style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        border: '1px solid #2a2a2a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 3v12M4 9l6-6 6 6" stroke="#555" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{
        color: '#888',
        fontSize: 14,
        fontFamily: 'Georgia, serif',
        marginBottom: 8,
        textAlign: 'center',
      }}>
        {dragOver ? 'Drop to add to workspace' : 'Your canvas is empty'}
      </div>
      <div style={{
        color: '#555',
        fontSize: 12,
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
        textAlign: 'center',
        maxWidth: 340,
        lineHeight: 1.7,
      }}>
        {workspaceGoal
          ? `Drag a file here, paste content, or ask Shift to help you with: "${workspaceGoal}"`
          : 'Drag a file here, paste content, or ask Shift to create something.'}
      </div>
      {atLimit && (
        <div style={{
          marginTop: 20,
          color: '#c46050',
          fontSize: 11,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          File limit reached. Remove an older file to add a new one.
        </div>
      )}
    </div>
  );
}

// ── Single tile ──────────────────────────────────────────
export function FileTile({ node, label, artifact, isKeyboardSelected, selectMode, isSelected, onClick, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const tileRef = useRef(null);
  const isGenerated = node.file_source === 'generated';
  const accent = FILE_NODE_COLORS[node.file_source] || FILE_NODE_COLORS.uploaded;
  const kind = classifyArtifact(artifact, node);
  const isImage = kind === 'image';
  const imageSrc = isImage ? (artifact?.content || null) : null;
  const highlighted = hovered || isKeyboardSelected || isSelected;

  // Scroll into view when keyboard-selected
  useEffect(() => {
    if (isKeyboardSelected && tileRef.current) {
      tileRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isKeyboardSelected]);

  return (
    <div
      ref={tileRef}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isGenerated ? 'rgba(200,169,110,0.04)' : '#07070a',
        border: `1px solid ${isSelected ? '#9b8fc4' : highlighted ? accent : '#272736'}`,
        borderRadius: 8,
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
        transform: highlighted ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: isKeyboardSelected ? `0 0 0 1px ${accent}44` : 'none',
      }}
    >
      {/* Visual area — image preview for images, context card for everything else */}
      <div style={{
        height: imageSrc ? 92 : 'auto',
        minHeight: imageSrc ? 92 : 52,
        background: imageSrc ? '#07070a' : 'transparent',
        borderBottom: imageSrc ? '1px solid #161616' : 'none',
        display: 'flex',
        alignItems: imageSrc ? 'center' : 'flex-start',
        justifyContent: imageSrc ? 'center' : 'flex-start',
        position: 'relative',
        overflow: 'hidden',
        padding: imageSrc ? 0 : '10px 10px 0',
      }}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={node.title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              background: `${accent}12`, border: `1px solid ${accent}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <KindIcon kind={kind} color={accent} />
            </div>
            <span style={{
              fontSize: 9, color: accent, fontFamily: 'Georgia, serif',
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
              {kind === 'html' ? 'page' : kind === 'svg' ? 'svg' : kind === 'code' ? 'code' : kind === 'link' ? 'link' : 'doc'}
            </span>
          </div>
        )}
        {/* Select mode: checkbox. Normal mode: label badge. */}
        {selectMode ? (
          <div style={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: 18,
            height: 18,
            borderRadius: 4,
            border: `1.5px solid ${isSelected ? '#9b8fc4' : '#555'}`,
            background: isSelected ? '#9b8fc4' : 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
          }}>
            {isSelected && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 5l2.5 2.5L8 3" />
              </svg>
            )}
          </div>
        ) : (
          <span style={{
            position: 'absolute',
            top: 6,
            left: 6,
            background: 'rgba(0,0,0,0.65)',
            color: accent,
            fontSize: 9,
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            letterSpacing: '0.06em',
            padding: '2px 6px',
            borderRadius: 3,
          }}>
            {label}
          </span>
        )}
        {/* Source dot — gold = Shift-generated, gray = uploaded.
            Hidden while hovering so the delete × can sit in the same
            top-right slot without competing for the eye. */}
        {!hovered && (
          <span
            title={isGenerated ? 'Generated by Shift' : 'Uploaded'}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: accent,
              opacity: isGenerated ? 1 : 0.6,
            }}
          />
        )}

        {/* Delete × — hover-reveal, top-right. Hard-deletes the file
            node so it disappears from the canvas + journey map. The
            underlying artifact is preserved in the user's library. */}
        {hovered && onDelete && (
          <button
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            title="Remove from workspace"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.7)',
              border: '1px solid #2a2a2a',
              color: '#888',
              fontSize: 14,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = '#c46050';
              e.currentTarget.style.borderColor = '#c46050';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = '#888';
              e.currentTarget.style.borderColor = '#2a2a2a';
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Title strip */}
      <div style={{
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 38,
      }}>
        <div style={{
          fontSize: 11,
          color: '#d4d4d4',
          fontFamily: 'Georgia, serif',
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {node.title || 'Untitled'}
        </div>
        <div style={{
          fontSize: 9,
          color: '#555',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          {kind === 'image' ? 'image' : kind === 'svg' ? 'svg' : kind === 'code' ? (artifact?.artifact_type || 'code') : kind === 'html' ? 'page' : kind === 'link' ? 'link' : 'doc'}
        </div>
      </div>
    </div>
  );
}

// ── Kind icon ────────────────────────────────────────────
export function KindIcon({ kind, color }) {
  const stroke = color || '#666';
  if (kind === 'image') {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    );
  }
  if (kind === 'code') {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
        <path d="M8 7l-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'link') {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
        <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'svg') {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
        <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinejoin="round" />
        <path d="M2 17l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'html') {
    // Browser-window glyph — three dots above a content area, the
    // visual hint that this tile renders to a real page.
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
        <rect x="3" y="4" width="18" height="16" rx="2" strokeLinejoin="round" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <circle cx="6" cy="6.5" r="0.6" fill={stroke} stroke="none" />
        <circle cx="8" cy="6.5" r="0.6" fill={stroke} stroke="none" />
        <circle cx="10" cy="6.5" r="0.6" fill={stroke} stroke="none" />
      </svg>
    );
  }
  // doc
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinejoin="round" />
      <polyline points="14 2 14 8 20 8" strokeLinejoin="round" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </svg>
  );
}

// ── New artifact dropdown ────────────────────────────────────
// Small menu with the creatable artifact kinds. Positioned below the
// "+ New" button in the Gallery header.
export function NewArtifactMenu({ onSelect, onClose }) {
  const options = [
    { kind: 'doc',  label: 'Document',  icon: '◎', desc: 'Markdown notes, plans, prose' },
    { kind: 'code', label: 'Code',      icon: '⟨⟩', desc: 'Scripts, snippets, formulas' },
    { kind: 'html', label: 'Page',      icon: '◻',  desc: 'Dashboard, website, visual' },
  ];

  return (
    <>
      {/* Invisible backdrop to close on click-outside */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 99 }}
      />
      <div style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        background: '#111',
        border: '1px solid #272736',
        borderRadius: 8,
        padding: 6,
        minWidth: 200,
        zIndex: 100,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {options.map(opt => (
          <button
            key={opt.kind}
            onClick={() => onSelect(opt.kind)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 10px',
              background: 'transparent',
              border: 'none',
              borderRadius: 5,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1e1e2a'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{
              fontSize: 13,
              color: '#c8a96e',
              fontFamily: 'Georgia, serif',
              width: 20,
              textAlign: 'center',
              flexShrink: 0,
            }}>
              {opt.icon}
            </span>
            <div>
              <div style={{
                fontSize: 11,
                color: '#d4d4d4',
                fontFamily: 'Georgia, serif',
                fontWeight: 600,
              }}>
                {opt.label}
              </div>
              <div style={{
                fontSize: 9,
                color: '#555',
                fontFamily: 'Georgia, serif',
                marginTop: 1,
              }}>
                {opt.desc}
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// ── Mode toggle button ───────────────────────────────────────
// Single segment in the Read/Edit segmented control.
export function ModeToggleButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'rgba(122,158,130,0.16)' : 'transparent',
        border: 'none',
        borderRight: label === 'Read' ? '1px solid #2a2a2a' : 'none',
        color: active ? '#7a9e82' : '#666',
        fontSize: 9,
        fontFamily: 'Georgia, serif',
        fontWeight: active ? 700 : 400,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 10px',
        cursor: 'pointer',
        transition: 'all 0.12s',
        lineHeight: 1,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#7a9e82'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#666'; }}
    >
      {label}
    </button>
  );
}
